import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { fetchArtwork, initiateCheckout, type Artwork } from '../api'
import { findShippingOption } from '../shipping'

export function Checkout() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const urlError = searchParams.get('error')

  const [artwork, setArtwork] = useState<Artwork | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [shippingMethod, setShippingMethod] = useState<'pickup' | 'ship'>('ship')
  const [error, setError] = useState<string | null>(
    urlError === 'payment_failed' ? 'Betaling feilet. Vennligst prøv igjen.'
    : urlError === 'payment_cancelled' ? 'Betaling ble avbrutt.'
    : null
  )

  useEffect(() => {
    if (!id) return
    fetchArtwork(id)
      .then(a => {
        if (!a) { setNotFound(true); return }
        setArtwork(a)
        // default to pickup if ship not available
        if (!a.canShip && a.canPickup) setShippingMethod('pickup')
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [id])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    const fd = new FormData(e.currentTarget)
    setSubmitting(true)
    try {
      const { url } = await initiateCheckout(id!, {
        buyerEmail: (fd.get('buyerEmail') as string).trim(),
        buyerName: (fd.get('buyerName') as string).trim(),
        buyerPhone: (fd.get('buyerPhone') as string)?.trim() || undefined,
        shippingMethod,
      })
      window.location.href = url
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Utsjekk feilet. Vennligst prøv igjen.')
      setSubmitting(false)
    }
  }

  if (loading) return <p className="state-loading container">Laster inn...</p>

  if (notFound || !artwork) {
    return (
      <div className="state-empty container">
        <p>Verk ikke funnet.</p>
        <Link to="/" style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'underline' }}>
          ← Tilbake til galleriet
        </Link>
      </div>
    )
  }

  const isSold = artwork.status !== 'available'
  const totalNok = artwork.priceNok + (shippingMethod === 'ship' ? artwork.shippingNok : 0)

  return (
    <main className="checkout container">
      <title>Kjøp — Originalene</title>
      <meta name="robots" content="noindex" />
      <Link to={`/artwork/${artwork.id}`} className="back-link">← Tilbake til verk</Link>

      <div className="checkout-layout">
        <div className="checkout-artwork">
          <div className="checkout-artwork-img">
            <img src={artwork.imageUrl} alt={artwork.title} />
          </div>
          <div className="checkout-artwork-info">
            <p className="checkout-artwork-title">{artwork.title}</p>
            <p className="checkout-artwork-artist">{artwork.artistName}</p>
            <p className="checkout-artwork-price">
              {artwork.priceNok.toLocaleString('nb-NO')} NOK
            </p>
          </div>
        </div>

        <div className="checkout-form-area">
          {isSold ? (
            <div className="checkout-sold">
              <p style={{ fontWeight: 600, marginBottom: '0.75rem' }}>
                Dette verket har allerede blitt solgt.
              </p>
              <Link to="/" style={{ textDecoration: 'underline', fontSize: '0.9rem' }}>
                Se andre verk →
              </Link>
            </div>
          ) : (
            <>
              <h1 className="checkout-title">Fullfør kjøpet</h1>
              <p className="checkout-subtitle">
                Du blir omdirigert til Stripe for å fullføre betalingen.
              </p>

              <form className="checkout-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="buyerName">Fullt navn</label>
                  <input
                    id="buyerName"
                    name="buyerName"
                    className="form-input"
                    required
                    autoComplete="name"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="buyerEmail">E-postadresse</label>
                  <input
                    id="buyerEmail"
                    name="buyerEmail"
                    type="email"
                    className="form-input"
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="buyerPhone">
                    Telefonnummer
                    {shippingMethod === 'ship' && (
                      <span style={{ color: 'var(--muted)', fontWeight: 400 }}> (valgfritt)</span>
                    )}
                  </label>
                  <input
                    id="buyerPhone"
                    name="buyerPhone"
                    type="tel"
                    className="form-input"
                    required={shippingMethod === 'pickup'}
                    autoComplete="tel"
                    placeholder="+47 000 00 000"
                  />
                </div>

                {/* Shipping method */}
                {(artwork.canShip || artwork.canPickup) && (
                  <div className="form-group">
                    <label className="form-label">Leveringsmåte</label>
                    <div className="shipping-options">
                      {artwork.canShip && (
                        <label className={`shipping-option${shippingMethod === 'ship' ? ' shipping-option--selected' : ''}`}>
                          <input
                            type="radio"
                            name="shippingMethod"
                            value="ship"
                            checked={shippingMethod === 'ship'}
                            onChange={() => setShippingMethod('ship')}
                          />
                          <span className="shipping-option-label">
                            {(() => {
                              const opt = findShippingOption(artwork.shippingOption)
                              return opt ? `${opt.carrier} ${opt.label}` : 'Frakt'
                            })()}
                          </span>
                          <span className="shipping-option-price">
                            {artwork.shippingNok.toLocaleString('nb-NO')} NOK
                          </span>
                        </label>
                      )}
                      {artwork.canPickup && (
                        <label className={`shipping-option${shippingMethod === 'pickup' ? ' shipping-option--selected' : ''}`}>
                          <input
                            type="radio"
                            name="shippingMethod"
                            value="pickup"
                            checked={shippingMethod === 'pickup'}
                            onChange={() => setShippingMethod('pickup')}
                          />
                          <span className="shipping-option-label">
                            Henting{artwork.pickupLocation ? ` — ${artwork.pickupLocation}` : ''}
                          </span>
                          <span className="shipping-option-price">Gratis</span>
                        </label>
                      )}
                    </div>
                    {shippingMethod === 'ship' && (
                      <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                        Leveringsadresse oppgis på neste side.
                      </p>
                    )}
                  </div>
                )}

                {/* Order summary */}
                <div className="checkout-summary">
                  <div className="checkout-summary-row">
                    <span>Kunstverk</span>
                    <span>{artwork.priceNok.toLocaleString('nb-NO')} NOK</span>
                  </div>
                  {shippingMethod === 'ship' && (
                    <div className="checkout-summary-row">
                      <span>Frakt</span>
                      <span>{artwork.shippingNok.toLocaleString('nb-NO')} NOK</span>
                    </div>
                  )}
                  <div className="checkout-summary-row checkout-summary-total">
                    <span>Totalt</span>
                    <span>{totalNok.toLocaleString('nb-NO')} NOK</span>
                  </div>
                </div>

                {error && <p className="form-error">{error}</p>}

                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting
                    ? 'Omdirigerer til Stripe…'
                    : `Betal ${totalNok.toLocaleString('nb-NO')} NOK`}
                </button>

                <p className="checkout-disclaimer">
                  Betalingen blir behandlet sikkert av Stripe.
                  {shippingMethod === 'ship' && ' Leveringsadresse oppgis på Stripes betalingsside.'}
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  )
}
